import { graphql, useFragment } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ErrorPolicy } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useDefaultSpacesForCreate } from '@confluence/space-utils/entry-points/defaultSpacesForCreate.ts';

import type {
	useSpaceEnabledContentTypesQuery$data,
	useSpaceEnabledContentTypesQuery$variables,
} from './__generated__/useSpaceEnabledContentTypesQuery.graphql.ts';
import type {
	useSpaceEnabledContentTypes_space$key,
	useSpaceEnabledContentTypes_space$data,
} from './__generated__/useSpaceEnabledContentTypes_space.graphql.ts';

const spaceEnabledContentTypesFragment = graphql`
	fragment useSpaceEnabledContentTypes_space on Space {
		id
		key
		spaceTypeSettings {
			enabledContentTypes {
				isBlogsEnabled
				isDatabasesEnabled
				isEmbedsEnabled
				isLivePagesEnabled
				isSlidesEnabled
				isWhiteboardsEnabled
				isFoldersEnabled
			}
		}
	}
`;

export function useSpaceEnabledContentTypesFragment(
	spaceFragmentRef: useSpaceEnabledContentTypes_space$key,
): ReturnType<typeof useSpaceEnabledContentTypes> {
	const space = useFragment(spaceEnabledContentTypesFragment, spaceFragmentRef);
	return getEnabledContentTypes(space.spaceTypeSettings?.enabledContentTypes, false);
}

const useSpaceEnabledContentTypesQuery = graphql`
	query useSpaceEnabledContentTypesQuery($spaceKey: String) {
		space(key: $spaceKey) {
			...useSpaceEnabledContentTypes_space @relay(mask: false)
		}
	}
`;

export function useSpaceEnabledContentTypes(spaceKey: string | undefined): {
	isCreateWhiteboardEnabled: boolean;
	isCreateBlogEnabled: boolean;
	isCreateDatabaseEnabled: boolean;
	isCreateSmartLinkEnabled: boolean;
	isCreateLiveDocEnabled: boolean;
	isCreateFolderEnabled: boolean;
	isCreateSlidesEnabled: boolean;
	isLoading: boolean;
} {
	const { data: defaultSpaceKeyData, loading: isDefaultSpaceKeyLoading } =
		useDefaultSpacesForCreate({
			skip: spaceKey !== undefined,
			variables: {
				promotedSpacesLimit: 1,
				otherSpacesLimit: 1,
				promotedSpaceKey: null,
			},
		});

	const spaceEnabledContentTypesQueryOptions = {
		errorPolicy: 'all' as ErrorPolicy,
		skip: !spaceKey && !defaultSpaceKeyData,
		variables: {
			spaceKey: spaceKey || defaultSpaceKeyData?.experimentalPromotedSpaces?.nodes?.[0]?.id,
		},
	};

	const { data: enabledContentTypesData, loading: isAnyContentTypesLoading } =
		useQueryNoFurtherUpdateInterop<
			useSpaceEnabledContentTypesQuery$data,
			useSpaceEnabledContentTypesQuery$variables
		>(useSpaceEnabledContentTypesQuery, spaceEnabledContentTypesQueryOptions);

	const enabledContentTypes =
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes;
	return getEnabledContentTypes(
		enabledContentTypes,
		isDefaultSpaceKeyLoading || isAnyContentTypesLoading,
	);
}

function getEnabledContentTypes(
	enabledContentTypes:
		| NonNullable<
				useSpaceEnabledContentTypes_space$data['spaceTypeSettings']
		  >['enabledContentTypes']
		| undefined,
	loading: boolean,
): ReturnType<typeof useSpaceEnabledContentTypes> {
	const hasUndefinedContentTypes =
		enabledContentTypes === undefined ||
		Object.values(enabledContentTypes).some((type) => type === undefined);

	const isLoading = loading || hasUndefinedContentTypes;

	return {
		isCreateWhiteboardEnabled: Boolean(enabledContentTypes?.isWhiteboardsEnabled),
		isCreateBlogEnabled: Boolean(enabledContentTypes?.isBlogsEnabled),
		isCreateDatabaseEnabled: Boolean(enabledContentTypes?.isDatabasesEnabled),
		isCreateSmartLinkEnabled: Boolean(enabledContentTypes?.isEmbedsEnabled),
		isCreateLiveDocEnabled: Boolean(enabledContentTypes?.isLivePagesEnabled),
		isCreateFolderEnabled: Boolean(enabledContentTypes?.isFoldersEnabled),
		isCreateSlidesEnabled: Boolean(enabledContentTypes?.isSlidesEnabled),
		isLoading,
	};
}
